import { Helmet as HelmetUntyped } from "react-helmet-async";
import type { ComponentType, PropsWithChildren } from "react";

// react-helmet-async v2 ships class-component types that don't line up cleanly
// with current @types/react JSX runtime, so we narrow it to a permissive
// functional component type to keep the file compiling.
const Helmet = HelmetUntyped as unknown as ComponentType<PropsWithChildren<unknown>>;

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogType?: "website" | "article";
  noindex?: boolean;
  jsonLd?: Record<string, unknown>;
}

const SITE_NAME = "Reqqy";
const DEFAULT_DESCRIPTION =
  "Reqqy is an AI-powered requirements clarity and ambiguity detection tool for product teams.";
const DEFAULT_OG_IMAGE = "https://reqqy.app/logo-with-text-dark.png";
const BASE_URL = "https://reqqy.app";

/**
 * Ensure preview crawlers (Facebook, LinkedIn, Twitter, Slack) receive an
 * absolute URL. Relative paths or protocol-relative URLs are silently dropped
 * by most scrapers, leaving previews without an image.
 */
function toAbsoluteUrl(url: string | undefined): string {
  if (!url) return DEFAULT_OG_IMAGE;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("//")) return `https:${url}`;
  return `${BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt,
  ogType = "website",
  noindex = false,
  jsonLd,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME}`;
  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : undefined;
  const absoluteImage = toAbsoluteUrl(ogImage);
  const imageAlt = ogImageAlt ?? title ?? SITE_NAME;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />

      {/* Canonical */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:secure_url" content={absoluteImage} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@reqqy" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* JSON-LD */}
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}
